import {
  AppInfo,
  Capture,
  CaptureEvent,
  CaptureEventIds,
  CaptureProperty,
  CapturePropertyIds,
  CapturePropertyTypes,
  DataConfirmationBeep,
  DataConfirmationLed,
  DataConfirmationMode,
  DataConfirmationRumble,
  DeviceInfo,
  SktErrors,
} from 'socketmobile-capturejs'
import { ref } from '@vue/reactivity'

interface DecodedDataValue {
  id: number
  name: string
  data: number[] | string
}

type ScanResultHandler = (scannedText: string) => void

const appInfo = {
  appId: process.env.VUE_APP_SOCKET_MOBILE_APP_ID ?? '',
  developerId: process.env.VUE_APP_SOCKET_MOBILE_DEVELOPER_ID ?? '',
  appKey: process.env.VUE_APP_SOCKET_MOBILE_APP_KEY ?? '',
}

const goodScanConfirmation =
  (DataConfirmationRumble.Good << 4) + (DataConfirmationBeep.Good << 2) + DataConfirmationLed.Green

const parentCapture = new Capture()
const captureDevices = new Map<string, Capture>()
const captureDeviceHandles = new Map<number, Capture>()

let isCaptureOpen = false
let captureSessionCount = 0
let scanResultHandler: ScanResultHandler | null = null
const isSocketMobileDeviceConnected = ref(false)

export function isConnectedSocketMobile(): boolean {
  return isSocketMobileDeviceConnected.value
}

export function setSocketMobileScanResultHandler(handler: ScanResultHandler | null): void {
  scanResultHandler = handler
}

export function clearSocketMobileScanResultHandler(handler: ScanResultHandler): void {
  if (scanResultHandler === handler) {
    scanResultHandler = null
  }
}

function syncSocketMobileDeviceConnectedState(): void {
  isSocketMobileDeviceConnected.value = captureDevices.size > 0
}

function assertSocketMobileAppInfoConfigured(): void {
  if (!appInfo.appId || !appInfo.developerId || !appInfo.appKey) {
    throw new Error(
      'Socket Mobile credentials are missing. Set VUE_APP_SOCKET_MOBILE_APP_ID, VUE_APP_SOCKET_MOBILE_DEVELOPER_ID, and VUE_APP_SOCKET_MOBILE_APP_KEY in your environment file.'
    )
  }
}

function parseDecodedDataToString(decodedData: DecodedDataValue | undefined): string | null {
  const payload = decodedData?.data
  if (!payload || (Array.isArray(payload) && payload.length === 0)) {
    return null
  }

  if (Array.isArray(payload)) {
    return payload.map((byte) => String.fromCharCode(byte)).join('').trim()
  }

  return payload.length > 0 ? payload.trim() : null
}

function setDataConfirmationMode(captureDevice: Capture, context: string): void {
  const property = new CaptureProperty(
    CapturePropertyIds.DataConfirmationMode,
    CapturePropertyTypes.Byte,
    DataConfirmationMode.ModeCapture
  )

  captureDevice.setProperty(property).catch((error) => {
    console.error(`Failed to set Socket Mobile ${context} data confirmation mode:`, error)
  })
}

function confirmDecodedData(captureDevice: Capture): void {
  const property = new CaptureProperty(
    CapturePropertyIds.DataConfirmationDevice,
    CapturePropertyTypes.Ulong,
    goodScanConfirmation
  )

  captureDevice.setProperty(property).catch((error) => {
    console.error('Failed to confirm Socket Mobile decoded data:', error)
  })
}

function resolveCaptureDeviceForEvent(handleIndex?: number): Capture | undefined {
  if (handleIndex !== undefined) {
    return captureDeviceHandles.get(handleIndex)
  }

  return captureDevices.size === 1 ? captureDevices.values().next().value : undefined
}

async function openCaptureDevice(deviceInfo: DeviceInfo): Promise<void> {
  const deviceCapture = new Capture()

  try {
    await deviceCapture.openDevice(deviceInfo.guid, parentCapture)
    captureDevices.set(deviceInfo.guid, deviceCapture)
    captureDeviceHandles.set(deviceCapture.clientOrDeviceHandle, deviceCapture)
    syncSocketMobileDeviceConnectedState()
    setDataConfirmationMode(deviceCapture, 'device')
  } catch (error) {
    console.error('Failed to open Socket Mobile device:', deviceInfo.guid, error)
  }
}

function handleDecodedDataEvent(event: CaptureEvent<DecodedDataValue>, handleIndex?: number): void {
  if (event.result === SktErrors.ESKT_CANCEL) {
    return
  }

  const scannedText = parseDecodedDataToString(event.value as DecodedDataValue)
  if (!scannedText) {
    console.warn('Socket Mobile decoded data was empty:', event.value)
    return
  }

  const deviceCapture = resolveCaptureDeviceForEvent(handleIndex)
  if (deviceCapture) {
    confirmDecodedData(deviceCapture)
  }

  if (!scanResultHandler) {
    return
  }

  try {
    scanResultHandler(scannedText)
  } catch (error) {
    console.error('Socket Mobile scan result handler failed:', error)
  }
}

function handleEventNotification(event: CaptureEvent<DeviceInfo | DecodedDataValue>, handle?: number): void {
  switch (event.id) {
    case CaptureEventIds.DeviceArrival:
      openCaptureDevice(event.value as DeviceInfo)
      break
    case CaptureEventIds.DeviceRemoval: {
      const deviceInfo = event.value as DeviceInfo
      const captureDevice = captureDevices.get(deviceInfo.guid)
      if (captureDevice) {
        captureDeviceHandles.delete(captureDevice.clientOrDeviceHandle)
      }
      captureDevices.delete(deviceInfo.guid)
      syncSocketMobileDeviceConnectedState()
      break
    }
    case CaptureEventIds.DecodedData:
      handleDecodedDataEvent(event as CaptureEvent<DecodedDataValue>, handle)
      break
    case CaptureEventIds.Error:
      console.error('Socket Mobile capture error event:', event)
      break
    default:
      console.warn('Socket Mobile capture event not handled:', event)
      break
  }
}

async function startParentCapture(): Promise<void> {
  assertSocketMobileAppInfoConfigured()
  await parentCapture.open(appInfo as AppInfo, handleEventNotification)
  isCaptureOpen = true
  setDataConfirmationMode(parentCapture, 'client')
}

export async function openSocketMobileCapture(): Promise<void> {
  captureSessionCount += 1
  if (isCaptureOpen) {
    return
  }

  try {
    await startParentCapture()
  } catch (error) {
    captureSessionCount = Math.max(0, captureSessionCount - 1)
    console.error('Failed to open Socket Mobile capture:', error)
    throw error
  }
}

export async function closeSocketMobileCapture(): Promise<void> {
  captureSessionCount = Math.max(0, captureSessionCount - 1)
  if (captureSessionCount > 0 || !isCaptureOpen) {
    return
  }

  captureDevices.clear()
  captureDeviceHandles.clear()
  syncSocketMobileDeviceConnectedState()

  try {
    await parentCapture.close()
  } catch (error) {
    console.error('Failed to close Socket Mobile capture:', error)
  }

  if (captureSessionCount > 0) {
    try {
      await startParentCapture()
    } catch (error) {
      console.error('Failed to re-open Socket Mobile capture after close race:', error)
    }
    return
  }

  isCaptureOpen = false
}
